import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const leftLinks = [
  { label: "Features", href: "#how" },
  { label: "Community", href: "#community" },
  { label: "Safety", href: "#faq" },
];
const rightLinks = [
  { label: "How it works", href: "#app-preview" },
  { label: "FAQ", href: "#faq" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToId = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  const linkCls =
    "text-[11px] uppercase tracking-[0.25em] text-neutral-300 hover:text-[#C6FF00] transition-colors font-semibold";

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto max-w-7xl px-6 md:px-10 h-20 grid grid-cols-2 md:grid-cols-[1fr_auto_1fr] items-center gap-6">
        {/* Left links (desktop) */}
        <div className="hidden md:flex items-center gap-8">
          {leftLinks.map((l) => (
            <a
              key={l.href + l.label}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={(e) => scrollToId(e, l.href)}
              className={linkCls}
            >
              {l.label}
            </a>
          ))}
        </div>

        {/* Centered brand */}
        <a
          href="#top"
          data-testid="brand-logo"
          className="flex flex-col items-center md:items-center text-left md:text-center group"
          onClick={(e) => scrollToId(e, "#top")}
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#C6FF00] shadow-[0_0_18px_#C6FF00]" />
            <span className="font-heading text-2xl md:text-3xl tracking-[0.05em] text-white leading-none">
              SWOLE<span className="text-[#C6FF00]">MATES</span>
            </span>
          </div>
          <span className="hidden md:block text-[9px] uppercase tracking-[0.45em] text-neutral-500 font-semibold mt-1">
            Swipe. <span className="text-[#C6FF00]/80">Set.</span> Match.
          </span>
        </a>

        {/* Right side: links + CTA (desktop) */}
        <div className="hidden md:flex items-center justify-end gap-8">
          {rightLinks.map((l) => (
            <a
              key={l.href + l.label}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={(e) => scrollToId(e, l.href)}
              className={linkCls}
            >
              {l.label}
            </a>
          ))}
          {!user && (
            <Link
              to="/login"
              data-testid="navbar-signin-link"
              className="text-[11px] uppercase tracking-[0.25em] text-neutral-300 hover:text-[#C6FF00] transition-colors font-semibold"
            >
              Sign In
            </Link>
          )}
          <Link
            to={user ? "/app/discover" : "/register"}
            data-testid="navbar-get-started-btn"
            className="inline-flex items-center gap-2 bg-[#C6FF00] hover:bg-[#AEE600] text-[#0A0A0A] font-bold text-[11px] uppercase tracking-[0.25em] px-5 py-3 rounded-md transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(198,255,0,0.25)] hover:shadow-[0_0_30px_rgba(198,255,0,0.5)]"
          >
            {user ? "Open App" : "Get Started"}
          </Link>
        </div>

        {/* Mobile: menu toggle right */}
        <button
          data-testid="navbar-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden text-white p-2 justify-self-end"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/5">
          <div className="container mx-auto px-6 py-6 flex flex-col gap-4">
            {[...leftLinks, ...rightLinks].map((l) => (
              <a
                key={"m-" + l.href + l.label}
                href={l.href}
                onClick={(e) => scrollToId(e, l.href)}
                className="text-base uppercase tracking-[0.2em] text-neutral-200 font-semibold py-2"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <Link
              to={user ? "/app/discover" : "/register"}
              data-testid="mobile-get-started-btn"
              onClick={() => setOpen(false)}
              className="bg-[#C6FF00] text-[#0A0A0A] font-bold text-sm uppercase tracking-[0.2em] px-5 py-3 rounded-md text-center"
            >
              {user ? "Open App" : "Get Started"}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
